<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { goalbookUrls } from '@/util/links.js'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import {
  getSSOProviderInfo,
  postUserLogin,
} from '@/authentication/api.ts'
import { useUserStore } from '@/authentication/user.store.js'
import { useRouterStore } from '@/router/router.store.ts'
import GbDialog from '@/components/GbDialog.vue'
import Checkbox from 'primevue/checkbox'
import { useQuery } from '@tanstack/vue-query'
import InputText from 'primevue/inputtext'
import GbButton from '@/components/GbButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import GoogleSignInButton from '@/components/GoogleSignInButton.vue'
import OneTimeCode from '@/authentication/OneTimeCode.vue'
import { SSOProviderInfo } from '@/authentication/auth_types.ts'
import { getCookie, setCrossSiteLogin } from '@/authentication/request_handler.ts'

// Static Values
const forgotPasswordUrl = goalbookUrls.forgotPassword

// Store Refs
const userStore = useUserStore()
const { ssoProviderIds, userEmail, hasGoogleOauth, enforceSSO } = storeToRefs(userStore)
const routerStore = useRouterStore()
const { reloginRequired } = storeToRefs(routerStore)

// HTTP Data & Status
const { data: ssoProviderInfo } = useQuery({ queryFn: getSSOProviderInfo, queryKey: [] })
const loginRequestSent = ref(false)
const loginCompleted = ref(false)
const otcCodeRequested = ref(false)
const otcRef = ref<InstanceType<typeof OneTimeCode> | null>(null)

const gblcs = ref('')

onMounted(async () => {
  // Set cookie for login forms
  await setCrossSiteLogin()
  gblcs.value = decodeURIComponent(getCookie('gblcs'))
})

// Form Values & Validation
const password = ref('')
const showPassword = ref(false)
const loginError = ref('')

// Computed SSO Provider Data
const ssoProviders = computed(() => {
  const ssoProviders: SSOProviderInfo[] = []

  ssoProviderIds.value?.forEach((id: string) => {
    const ssoProviderInfoForId = ssoProviderInfo?.value?.[id] as SSOProviderInfo | undefined
    if (ssoProviderInfoForId) {
      ssoProviders.push(ssoProviderInfoForId)
    }
  })

  return ssoProviders
})
const hasSSOProviders = computed(() => !!ssoProviders.value?.length)

// Analytics
const analytics = getAnalyticsTracker()
analytics.sendEvent('Login Modal Shown')

async function attemptLogin() {
  loginRequestSent.value = true
  try {
    await postUserLogin(userEmail.value, password.value, gblcs.value)
    loginCompleted.value = true
    await routerStore.handleReloginAttempt(loginCompleted.value)
    routerStore.loginSuccess = true
  } catch (e) {
    console.error(e)
    routerStore.loginSuccess = false
    loginError.value =
      'There was a problem with your login request. You may need to try again later.'
  } finally {
    loginRequestSent.value = false
  }
}

async function onOtcLoginSuccess() {
  loginCompleted.value = true
  await routerStore.handleReloginAttempt(loginCompleted.value)
  routerStore.loginSuccess = true
}

function handleVisibilityChange(isVisible: boolean) {
  if (!isVisible && !loginCompleted.value) {
    analytics?.sendEvent('Login Modal Dismissed')
    routerStore.handleReloginAttempt(loginCompleted.value)
    routerStore.loginDismissed = true
  }
  if (!isVisible) {
    if (typeof otcRef.value?.reset === 'function') otcRef.value.reset()
    otcCodeRequested.value = false
    loginCompleted.value = false
  }
}
</script>

<template>
  <GbDialog
    dialog-id="login-modal"
    :visible="reloginRequired"
    @update:visible="handleVisibilityChange"
    @hide="() => handleVisibilityChange(false)"
    header="Sign In to Your Account"
    role="alertdialog"
    aria-describedby="login-message"
    position="top"
  >
    <template #bodyContent>
      <GbInlineMessage gbTheme="primary" class="mb-4" :inline="false" id="login-message">
        For your data protection, please log in to access this feature.
      </GbInlineMessage>
      <form class="text-xl" @submit.prevent="attemptLogin" v-if="!enforceSSO">
        <input type="hidden" name="authenticity_token" :value="gblcs" />
        <label for="current-password" class="mb-2 mt-5 font-regular">Password</label>
        <InputText
          id="current-password"
          :type="showPassword ? 'text' : 'password'"
          v-model="password"
          autocomplete="current-password"
          class="mb-2 mt-2 w-full"
        />

        <div class="form-group mb-2">
          <label for="passwordCheckbox" class="font-regular">
            <Checkbox
              v-model="showPassword"
              :binary="true"
              inputId="passwordCheckbox"
              gbTheme="green"
              size="small"
            />
            Show Password
          </label>
        </div>

        <div class="flex items-center justify-between">
          <GbButton
            type="submit"
            label="Sign In"
            gbTheme="greenPrimary"
            size="large"
            class="my-2 h-[3rem] w-[12rem]"
            :disabled="!password || loginRequestSent"
          />

          <a :href="forgotPasswordUrl" class="text-link" target="_blank"> Forgot your password? </a>
        </div>

        <GbInlineMessage v-if="loginError" :duration="4000" gbTheme="red" class="my-4">
          {{ loginError }}
        </GbInlineMessage>
      </form>

      <div v-if="!enforceSSO">
        <p class="relative border-b border-dotted border-gray-400 mb-[30px] mx-[30px] h-5">
          <span class="relative text-center h-10 w-10 block mx-auto top-[5px] bg-surface-50 text-xl italic text-gray-700">or</span>
        </p>
      </div>

      <OneTimeCode
        ref="otcRef"
        :email="userEmail"
        :gblcs="gblcs"
        :disabled="loginRequestSent"
        @login-success="onOtcLoginSuccess"
        @otc-requested="otcCodeRequested = $event"
      />

      <div v-if="(hasSSOProviders || hasGoogleOauth) && !otcCodeRequested">
        <div class="flex flex-col items-center">
          <template v-for="provider in ssoProviders" :key="provider.id">
            <form method="POST" :action="provider.url">
              <input type="hidden" name="authenticity_token" :value="gblcs" />
              <GbButton type="submit" gbTheme="grayOutlineSecondary" size="large" class="mb-4 bg-white transition-[background-color,border-color,box-shadow] duration-[218ms] hover:bg-[#f0f0f0] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)]">
                Sign in through {{ provider.name }}
              </GbButton>
            </form>
          </template>

          <GoogleSignInButton v-if="hasGoogleOauth" />
        </div>
      </div>
    </template>
  </GbDialog>
</template>

